/**
 * Lista de transações ecommerce (rota /ecommerce).
 * @module EcommerceListPage
 */

import { memo, type ReactNode } from "react";
import { Alert, Stack } from "@mui/material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import type { Organization, Patient } from "fhir/r5";
import { LargeContentBox } from "@/components/common";
import { PageHeader } from "@/components/common/PageHeader";
import type {
  EcommerceWhatsAppTarget,
  UseEcommerceOrganizationPersonsResult,
  UseEcommerceWhatsAppEndpointResult,
} from "src-next/modules/ecommerce";
import { PersonBatchRegisterTrigger } from "src-next/modules/person-batch-register";
import { EcommercePatientsListSection } from "./EcommercePatientsListSection";
import EcommercePatientsListSkeleton from "../components/EcommercePatientsListSkeleton";

interface EcommerceListPageProps {
  organization: Organization;
  whatsAppStatusChip: ReactNode;
  whatsAppChannel: UseEcommerceWhatsAppEndpointResult;
  listRevision: number;
  organizationPersons: UseEcommerceOrganizationPersonsResult;
  onOpenManage: (
    patient: Patient,
    transactionId?: string,
    listSearch?: string,
  ) => void;
  onSendWhatsApp: (
    input: Patient[] | EcommerceWhatsAppTarget[],
    options?: { onSent?: () => void },
  ) => void;
}

export const EcommerceListPage = memo(function EcommerceListPage({
  organization,
  whatsAppStatusChip,
  whatsAppChannel,
  listRevision,
  organizationPersons,
  onOpenManage,
  onSendWhatsApp,
}: EcommerceListPageProps) {
  return (
    <LargeContentBox>
      <Stack spacing={3} sx={{ width: "100%" }}>
        <PageHeader
          title="Ecommerce"
          icon={ShoppingCartOutlinedIcon}
          description="Acompanhe transações, pacientes com consentimento e clientes cadastrados. Envie WhatsApp e libere compras quando fizer sentido."
          action={
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              justifyContent="flex-end"
              flexWrap="wrap"
              useFlexGap
            >
              <PersonBatchRegisterTrigger
                organizationId={organization.id}
                onFinished={organizationPersons.refetch}
              />
              {whatsAppStatusChip}
            </Stack>
          }
        />

        <EcommercePatientsListSection
          organization={organization}
          onOpenPage={onOpenManage}
          listRevision={listRevision}
          organizationPersons={organizationPersons}
          whatsAppChannel={whatsAppChannel}
          onSendWhatsApp={onSendWhatsApp}
        />
      </Stack>
    </LargeContentBox>
  );
});

export const EcommerceListPageSkeleton = memo(
  function EcommerceListPageSkeleton({
    action,
  }: {
    action?: ReactNode;
  }) {
    return (
      <LargeContentBox>
        <Stack spacing={3} sx={{ width: "100%" }}>
          <PageHeader
            title="Ecommerce"
            icon={ShoppingCartOutlinedIcon}
            description="Acompanhe transações, pacientes com consentimento e clientes cadastrados. Envie WhatsApp e libere compras quando fizer sentido."
            action={action}
          />
          <EcommercePatientsListSkeleton />
        </Stack>
      </LargeContentBox>
    );
  },
);

export const EcommerceOrganizationUnavailable = memo(
  function EcommerceOrganizationUnavailable() {
    return (
      <LargeContentBox>
        <Alert severity="info">
          A organização selecionada não é do tipo ecommerce.
        </Alert>
      </LargeContentBox>
    );
  },
);
